/**
 * Notification-channel fixtures — the port of the DOORS (N6, G-ALM-08,
 * webhook first), not of any live delivery. The server keeps a webhook-channel
 * registry with per-severity routing and answers six doors under ONE key —
 * `notify.manage`, reads included (plan D8: a channel URL may carry a token
 * in its query, so a viewer that must not write does not read either). This
 * file ports the rules those doors enforce so the dev console and the real
 * façade tell the operator the same story:
 *
 *  - `channel-kind-unsupported` / `notification-url-invalid` / the 404 word —
 *    the refusal sentences are the server's (`routers/notifications.py`), so
 *    a fixture refusal and a real one read the same on screen.
 *  - the URL rule is `notify.valid_url`'s port: http/https with a host, the
 *    pure rule, nothing smarter.
 *  - the feed's order and its `last_error` shape are the store's/worker's:
 *    `created_at` DESC with a `delivery_id` DESC tie-break, and a failed row's
 *    error is a house code plus the exception's TYPE — never its text.
 *
 * The named divergence (plan D4, ALM-05's own): nothing here DELIVERS. The
 * fixture has no notify worker — an intent minted by `:test` stays `pending`
 * and the panel says so. The three seed deliveries tell all three lifecycle
 * stories so the feed never renders a fabricated success.
 */

import type { NotificationChannel, NotificationDelivery } from '../api/types';
import { isoAgo } from './devices';

/** `NOTIFICATION_CHANNEL_KINDS` in `acs_service/models.py`. */
export const FIXTURE_CHANNEL_KINDS = ['webhook'] as const;

/** `NOTIFICATION_MIN_SEVERITIES` in `acs_service/models.py`. */
export const FIXTURE_MIN_SEVERITIES = ['critical', 'warning', 'info'] as const;

/** The door's feed bound (`DELIVERY_FEED_LIMIT` in `routers/notifications.py`). */
export const DELIVERY_FEED_LIMIT = 200;

/**
 * The named human the fixture records as `created_by` — the same stand-in
 * `INCIDENT_OPERATOR` plays for N5.
 */
export const NOTIFY_OPERATOR = 'user:console-owner';

/** The door's 404 wording, verbatim (`routers/notifications.py::_require_channel`). */
export function unknownChannelDetail(channelId: string): string {
  return `no notification channel with id '${channelId}' is known`;
}

/** The 422 wording of a kind nothing delivers (`_vet_channel_body`). */
export function channelKindUnsupportedDetail(kind: string): string {
  return `channel kind '${kind}' is not built; accepted kinds: webhook`;
}

/** The 422 wording of a non-http(s) URL (`_vet_channel_body` → `notify.valid_url`). */
export const NOTIFY_URL_INVALID_DETAIL = 'a notification channel URL must be an http(s) URL';

/** The 422 wording of an unknown severity floor (`_vet_channel_body`). */
export const MIN_SEVERITY_DETAIL = 'min_severity must be one of critical, warning, info';

/**
 * The port of `acs_service/notify.py::valid_url`: a notification URL is an
 * http(s) URL with a host. The `URL` parser treats `http://` with an empty
 * host as invalid, which is the same call the pure rule makes.
 */
export function fixtureUrlValid(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname !== '';
  } catch {
    return false;
  }
}

/** The create/update clock: the fixture stamps like the server does (ISO Z). */
export function fixtureNotifyClock(now: Date = new Date()): string {
  return now.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

const SEED_CHANNEL_WITH_SECRET: NotificationChannel = {
  channel_id: 'ch:7f1a2b3c4d5e',
  kind: 'webhook',
  name: 'وب‌هوک مرکز عملیات',
  url: 'https://hooks.noc.example/acs/alarms',
  min_severity: 'warning',
  enabled: true,
  has_secret: true,
  created_by: NOTIFY_OPERATOR,
  created_at: isoAgo(72),
  updated_at: isoAgo(48),
};

const SEED_CHANNEL_DISABLED: NotificationChannel = {
  channel_id: 'ch:2b9d8e7f6a5c',
  kind: 'webhook',
  name: 'وب‌هوک آزمایشی',
  url: 'http://127.0.0.1:9000/hook',
  min_severity: 'info',
  enabled: false,
  has_secret: false,
  created_by: NOTIFY_OPERATOR,
  created_at: isoAgo(24),
  updated_at: isoAgo(24),
};

export const FIXTURE_CHANNELS: readonly NotificationChannel[] = [
  SEED_CHANNEL_WITH_SECRET,
  SEED_CHANNEL_DISABLED,
];

/**
 * Three seed deliveries, one per lifecycle word — the feed never renders a
 * fabricated success. The failed row's `last_error` is the worker's own shape:
 * a house code plus the exception's TYPE (`shape.failure_text`), never text.
 */
export const FIXTURE_DELIVERIES: readonly NotificationDelivery[] = [
  {
    delivery_id: 'ntf:seed3pending1',
    channel_id: SEED_CHANNEL_DISABLED.channel_id,
    channel_name: SEED_CHANNEL_DISABLED.name,
    kind: 'test',
    alarm_id: null,
    alarm_type: '',
    severity: '',
    acs_device_id: '',
    occurred_at: isoAgo(1),
    status: 'pending',
    attempts: 0,
    last_error: null,
    next_attempt_at: isoAgo(1),
    created_at: isoAgo(1),
    finished_at: null,
  },
  {
    delivery_id: 'ntf:seed2failed01',
    channel_id: SEED_CHANNEL_WITH_SECRET.channel_id,
    channel_name: SEED_CHANNEL_WITH_SECRET.name,
    kind: 'alarm',
    alarm_id: 'evt:optical:acs-device:HA-992:rx:2026-10-30',
    alarm_type: 'optical.degraded',
    severity: 'warning',
    acs_device_id: 'acs-device:HA-992',
    occurred_at: isoAgo(6),
    status: 'failed',
    attempts: 5,
    last_error: 'webhook-http-error:HTTPStatusError',
    next_attempt_at: null,
    created_at: isoAgo(6),
    finished_at: isoAgo(5),
  },
  {
    delivery_id: 'ntf:seed1delivere',
    channel_id: SEED_CHANNEL_WITH_SECRET.channel_id,
    channel_name: SEED_CHANNEL_WITH_SECRET.name,
    kind: 'alarm',
    alarm_id: 'evt:onu:los:olt-1:1:2:ACS-1001',
    alarm_type: 'onu.los',
    severity: 'critical',
    acs_device_id: 'acs-device:HA-1001',
    occurred_at: isoAgo(20),
    status: 'delivered',
    attempts: 1,
    last_error: null,
    next_attempt_at: null,
    created_at: isoAgo(20),
    finished_at: isoAgo(20),
  },
];
